import type {
  ProductConfig,
  ProductDefinition,
  JobPostingDefinition,
  UpgradeDefinition,
  UpgradeRequirements,
} from '../../state/reducers'
import type {
  Upgrade,
  UpgradesPackage,
} from '../../state/reducers/packages/packages'
import type {
  JobPostingEntitlement,
  JobPostingUpgradeEntitlement,
} from '../../state/reducers/entitlement/entitlement'

type Definable =
  | JobPostingEntitlement
  | JobPostingUpgradeEntitlement

type EntryValue = string | Object | Array<string | Object>

const sortAtoZ = (a, b) => a.toString().localeCompare(b.toString())

const isArrayOfStringables = (value: EntryValue) =>
  Array.isArray(value) && value.every(val => val.toString !== undefined)

const sortedObjectEntries = (obj: Object): Array<[string, any]> =>
  Object.entries(obj).sort(([key1], [key2]) => key1.localeCompare(key2))

const combineEntries = ([key, value]: [string, EntryValue]) => // @ts-ignore
  `${key}{${isArrayOfStringables(value) ? value.join('|') : value}}`

const getUpgradesDefinition = (
  suggestible: any,
): UpgradeDefinition => {
  if (!suggestible.upgrades || suggestible.upgrades.length === 0) return null

  return suggestible.upgrades.reduce(
    (acc, { features, sites }: Upgrade) => ({
      ...acc,
      features: [...new Set([...acc.features, ...features])],
      sites: [...new Set([...acc.sites, ...(sites || [])])],
    }),
    {
      features: [],
      sites: [],
    },
  )
}

const getSuggestibleDefinition = (
  suggestible: any,
): ProductDefinition => {
  let definition: ProductDefinition = null

  const upgradeDefinition: UpgradeDefinition = getUpgradesDefinition(
    suggestible,
  )

  if (suggestible.jobPosting) {
    let name: string
    let type: string

      // eslint-disable-next-line 
      // @ts-ignore
    ({ name, type, ...definition } = suggestible.jobPosting)

    if (!upgradeDefinition) {
      // No upgrades available for this package, we only need to return the definition from the
      // package's job posting
      // @ts-ignore
      return definition
    }
  } else {
    // No job posting definition, so we only need to return the upgrades defintion from the package
    // @ts-ignore
    return upgradeDefinition
  }

  // Both job posting and upgrades definition available; merging the two and returning the result
  const features = [
    ...new Set(upgradeDefinition.features.concat(definition.features)),
  ]

  const publishOn = [
    ...new Set(upgradeDefinition.sites.concat(definition.publishOn)),
  ]

  return { ...definition, features, publishOn }
}

const getProductDefinition = (product: ProductConfig): JobPostingDefinition => {
  // @ts-ignore
  const { name, type, requiredFeatures, requiredSites, ...definition } = product
  // @ts-ignore
  return definition
}

const getDefinition = (entity: Definable): ProductDefinition => {
  if (entity.product) {// @ts-ignore
    return getProductDefinition(entity.product)
  }

  return getSuggestibleDefinition(entity)
}

const getComposed = (definition: ProductDefinition) =>
  sortedObjectEntries(definition)
    .map(
      ([key, value]: [
        string,
        string[] | string | Object | typeof undefined,
      ]) => {
        let returnValue = [key, '']

        // eslint-disable-next-line default-case
        switch (typeof value) {
          case 'number':
          case 'string':// @ts-ignore
            returnValue = [key, value]
            break

          case 'object': {
            if (isArrayOfStringables(value)) {
              returnValue = [key, value.sort(sortAtoZ)]
            } else {
              returnValue = [
                key,// @ts-ignore
                sortedObjectEntries(value).map(combineEntries),
              ]
            }

            break
          }
        }

        return returnValue
      },
    )
    .filter(([, value]) => value)
    // @ts-ignore
    .map(combineEntries)

const getDefinitionId = (definition: ProductDefinition): string => {
  if (!definition) return ''

  const composed = getComposed(definition)
  return Buffer.from(composed.join(',')).toString('base64')
}

export const getPackageDefinitionId = (suggestible: Definable): string => {
  const definition = getDefinition(suggestible)

  return getDefinitionId(definition)
}

export const getProductDefinitionId = (product: ProductConfig): string => {
  const definition = getProductDefinition(product)

  // @ts-ignore
  return getDefinitionId(definition)
}

export const packageDefinitionsAreEqual = (
  definable1: Definable,
  definable2: Definable,
): boolean =>
  getPackageDefinitionId(definable1) === getPackageDefinitionId(definable2)

export const productDefinitionsAreEqual = (
  config1: ProductConfig,
  config2: ProductConfig,
): boolean => {
  if (config1.type !== config2.type) return false

  return getProductDefinitionId(config1) === getProductDefinitionId(config2)
}
